// Guided image filter (He, Sun & Tang 2010/2013) - used here to refine a
// coarse, low-res predicted alpha mask against the original full-res photo.
// The local linear model q = a*I + b forces the output to follow the
// guide image's real edges (snapping soft/blurry predicted boundaries to
// true high-frequency detail like hair or fabric edges) while smoothing
// within regions where the guide is roughly uniform.
//
// O(W*H) regardless of radius via a summed-area table (integral image) box
// filter, so this stays fast even on multi-megapixel phone-camera photos.

function boxFilter(data: Float32Array, w: number, h: number, r: number): Float32Array {
    // Separable box filter via 1D prefix sums (horizontal pass, then vertical).
    const tmp = new Float32Array(w * h);
    const out = new Float32Array(w * h);

    // Horizontal pass.
    for (let y = 0; y < h; y++) {
        const rowOff = y * w;
        let sum = 0;
        // Build a running sum with clamped edge handling.
        for (let x = -r; x <= r; x++) {
            const xc = Math.min(w - 1, Math.max(0, x));
            sum += data[rowOff + xc];
        }
        tmp[rowOff] = sum;
        for (let x = 1; x < w; x++) {
            const addX = Math.min(w - 1, x + r);
            const subX = Math.max(0, x - r - 1);
            sum += data[rowOff + addX] - data[rowOff + subX];
            tmp[rowOff + x] = sum;
        }
    }

    // Vertical pass.
    for (let x = 0; x < w; x++) {
        let sum = 0;
        for (let y = -r; y <= r; y++) {
            const yc = Math.min(h - 1, Math.max(0, y));
            sum += tmp[yc * w + x];
        }
        out[x] = sum;
        for (let y = 1; y < h; y++) {
            const addY = Math.min(h - 1, y + r);
            const subY = Math.max(0, y - r - 1);
            sum += tmp[addY * w + x] - tmp[subY * w + x];
            out[y * w + x] = sum;
        }
    }

    const windowArea = (2 * r + 1) * (2 * r + 1);
    for (let i = 0; i < out.length; i++) out[i] /= windowArea;
    return out;
}

/**
 * @param guide grayscale guide image, values in [0, 1], length w*h
 * @param input signal to refine (e.g. upsampled alpha), values in [0, 1], length w*h
 */
export function guidedFilter(
    guide: Float32Array,
    input: Float32Array,
    w: number,
    h: number,
    radius = 4,
    eps = 0.01
): Float32Array {
    const meanI = boxFilter(guide, w, h, radius);
    const meanP = boxFilter(input, w, h, radius);

    const guideP = new Float32Array(w * h);
    const guideSq = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) {
        guideP[i] = guide[i] * input[i];
        guideSq[i] = guide[i] * guide[i];
    }
    const corrIp = boxFilter(guideP, w, h, radius);
    const corrII = boxFilter(guideSq, w, h, radius);

    const a = new Float32Array(w * h);
    const b = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) {
        const varI = corrII[i] - meanI[i] * meanI[i];
        const covIp = corrIp[i] - meanI[i] * meanP[i];
        a[i] = covIp / (varI + eps);
        b[i] = meanP[i] - a[i] * meanI[i];
    }

    const meanA = boxFilter(a, w, h, radius);
    const meanB = boxFilter(b, w, h, radius);

    const q = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) {
        q[i] = Math.min(1, Math.max(0, meanA[i] * guide[i] + meanB[i]));
    }
    return q;
}
